"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Twitter, Linkedin, TrendingUp, ChevronUp } from "lucide-react";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const handleContactClick = (e: React.MouseEvent) => {
    const contactSection = document.getElementById("contact") || document.getElementById("contact-form");
    if (contactSection) {
      e.preventDefault();
      contactSection.scrollIntoView({
        behavior: "smooth"
      });
    }
  };
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3].map((_, i) => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackLinks = [{
      label: "Showcase",
      href: "#"
    }, {
      label: "AI Process",
      href: "#"
    }, {
      label: "Contact Us",
      href: "/Contact"
    }];
    return <ul className="space-y-3">
        {fallbackLinks.map((link, i) => <li key={i} data-index={i}>
            <Link to={link.href} className="text-sm text-muted-foreground hover:text-primary transition-colors font-mono">
              {link.label}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground hover:text-primary transition-colors font-mono">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const businessDescription = "aicommercials.ai leverages advanced generative AI to produce cinematic social media commercials that capture attention and drive conversions at scale.";
  const currentYear = new Date().getFullYear();
  const legalLinks = [{
    label: "Privacy Policy",
    href: "#"
  }, {
    label: "Terms of Service",
    href: "#"
  }];
  const socialLinks = [{
    icon: Twitter,
    href: "#",
    label: "X / Twitter"
  }, {
    icon: Linkedin,
    href: "#",
    label: "LinkedIn"
  }, {
    icon: TrendingUp,
    href: "#",
    label: "Performance Stats"
  }];
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  return <section data-section-id="226694" id="footer" className="relative bg-background pt-16 pb-8">
      <Separator className="absolute top-0 left-0 w-full h-1 bg-primary shadow-[0_0_15px_rgba(0,255,255,0.3)]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <img src="/img/capita_logo_footer.png" alt="aicommercials.ai" className="h-7 w-auto" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed font-mono pe-4">
              {businessDescription}
            </p>
          </div>

          <div>
            <h3 className="font-default font-bold text-foreground mb-6 text-lg tracking-tight uppercase">
              Navigation
            </h3>
            <MenuProvider menu_id="footer_menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div>
            <h3 className="font-default font-bold text-foreground mb-6 text-lg tracking-tight uppercase">
              Legal
            </h3>
            <ul className="space-y-3">
              {legalLinks.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.href} className="text-sm text-muted-foreground hover:text-primary transition-colors font-mono">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div>
            <h3 className="font-default font-bold text-foreground mb-6 text-lg tracking-tight uppercase">
              Connect
            </h3>
            <div className="flex flex-wrap gap-4">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} data-index={i} to={social.href} className="flex items-center justify-center size-10 rounded-full bg-card border border-border/50 text-muted-foreground hover:bg-primary hover:text-primary-foreground hover:shadow-[0_0_10px_rgba(0,255,255,0.4)] transition-all duration-300 backdrop-blur-sm" aria-label={social.label}>
                    <Icon className="size-4" />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-border/30 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-sm text-muted-foreground font-mono text-center md:text-right">
            © {currentYear} aicommercials.ai. All rights reserved.
          </p>
          <Button variant="outline" size="sm" onClick={scrollToTop} className="font-mono rounded-full px-6 border-border/50 hover:bg-primary hover:text-primary-foreground transition-all duration-300">
            Back to Top
            <ChevronUp className="size-4" />
          </Button>
        </div>
      </div>
    </section>;
}